import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  createFilterOptions,
} from '@mui/material';
import { Add, Search, Delete, ShoppingCart } from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { salesAPI, customersAPI, productsAPI, inventoryAPI, unitProductsAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const defaultOrderValues = {
  customer_id: '',
  warehouse_id: '',
  notes: '',
  items: [{ product_id: '', unit_product_id: '', quantity: 1, unit_price: 0 }],
  discount_percent: 0,
  tax_percent: 10,
  shipping_cost: 0,
};

const ORDER_STATUS_OPTIONS = ['draft', 'confirmed', 'processing', 'delivered', 'paid', 'cancelled', 'returned'];
const NEW_CUSTOMER_OPTION_ID = '__new_customer__';
const filterCustomers = createFilterOptions();

const unitOptionLabel = (unit) => {
  if (!unit) return '';
  const details = [
    unit.chassis_number,
    unit.color,
    unit.year,
    unit.condition?.replace(/_/g, ' '),
  ].filter(Boolean);
  return details.join(' • ');
};

const CreateOrderDialog = ({ open, onClose, onSaved, initialCustomer }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unitsByRow, setUnitsByRow] = useState({});
  const [unitsLoadingByRow, setUnitsLoadingByRow] = useState({});
  const [inventoryByWarehouse, setInventoryByWarehouse] = useState({});

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: defaultOrderValues });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const items = watch('items') || [];
  const warehouseId = watch('warehouse_id');
  const discPct = parseFloat(watch('discount_percent')) || 0;
  const taxPct = parseFloat(watch('tax_percent')) || 0;

  // Get all selected unit IDs across all rows to hide from dropdowns
  const selectedUnitIds = useMemo(() => {
    return items.map(item => item.unit_product_id).filter(Boolean);
  }, [items]);

  const loadUnitsForRow = useCallback(async (rowKey, productId, selectedWarehouseId) => {
    if (!productId || !selectedWarehouseId) {
      setUnitsByRow((prev) => ({ ...prev, [rowKey]: [] }));
      setUnitsLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
      return;
    }

    setUnitsLoadingByRow((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const { data } = await unitProductsAPI.getAll({
        page: 1,
        limit: 200,
        product_id: productId,
        warehouse_id: selectedWarehouseId,
        status: 'in_stock',
      });
      setUnitsByRow((prev) => ({ ...prev, [rowKey]: data.data || [] }));
    } catch {
      setUnitsByRow((prev) => ({ ...prev, [rowKey]: [] }));
    } finally {
      setUnitsLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    let active = true;
    reset(defaultOrderValues);
    setUnitsByRow({});
    setUnitsLoadingByRow({});
    setBootstrapLoading(true);

    Promise.all([
      customersAPI.getAll({ limit: 500 }),
      productsAPI.getAll({ limit: 500 }),
      inventoryAPI.getWarehouses(),
      inventoryAPI.getAll({ limit: 1000 }),
    ])
      .then(([customersResponse, productsResponse, warehousesResponse, inventoryResponse]) => {
        if (!active) return;
        const loadedCustomers = customersResponse.data.data || [];
        if (initialCustomer?.id && !loadedCustomers.some((customer) => customer.id === initialCustomer.id)) {
          loadedCustomers.unshift(initialCustomer);
        }
        setCustomers(loadedCustomers);
        setProducts(productsResponse.data.data || []);
        setFilteredProducts(productsResponse.data.data || []);
        setWarehouses(warehousesResponse.data.data || []);
        
        // Build inventory lookup by warehouse for product filtering
        const inventoryData = inventoryResponse.data.data || [];
        const byWarehouse = {};
        inventoryData.forEach(item => {
          if (!byWarehouse[item.warehouse_id]) {
            byWarehouse[item.warehouse_id] = new Set();
          }
          if (item.quantity > 0) {
            byWarehouse[item.warehouse_id].add(item.product_id);
          }
        });
        setInventoryByWarehouse(byWarehouse);
        
        if (initialCustomer?.id) {
          setValue('customer_id', initialCustomer.id, { shouldValidate: true, shouldDirty: true });
        }
      })
      .catch(() => {
        if (active) toast.error('Failed to load sales setup data');
      })
      .finally(() => {
        if (active) setBootstrapLoading(false);
      });

    return () => {
      active = false;
    };
  }, [initialCustomer, open, reset, setValue]);

  const customerOptions = useMemo(() => ([
    ...customers,
    {
      id: NEW_CUSTOMER_OPTION_ID,
      first_name: 'New',
      last_name: 'Customer',
      customer_code: 'Create from Customers page',
    },
  ]), [customers]);

  const subtotal = (items || []).reduce((sum, item) => sum + (parseFloat(item.unit_price) || 0), 0);
  const discount = subtotal * discPct / 100;
  const tax = (subtotal - discount) * taxPct / 100;
  const total = subtotal - discount + tax + (parseFloat(watch('shipping_cost')) || 0);

  const handleWarehouseSelection = async (value) => {
    setValue('warehouse_id', value, { shouldValidate: true, shouldDirty: true });

    // Filter products to only those with stock in selected warehouse
    if (value && inventoryByWarehouse[value]) {
      const productIdsInWarehouse = inventoryByWarehouse[value];
      const filtered = products.filter(p => productIdsInWarehouse.has(p.id));
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }

    // Clear all unit selections and reload units
    await Promise.all(fields.map(async (field, idx) => {
      setValue(`items.${idx}.unit_product_id`, '');
      setValue(`items.${idx}.product_id`, '');
      const productId = items[idx]?.product_id;
      await loadUnitsForRow(field.id, productId, value);
    }));
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        ...data,
        discount_percent: parseFloat(data.discount_percent) || 0,
        tax_percent: parseFloat(data.tax_percent) || 0,
        shipping_cost: parseFloat(data.shipping_cost) || 0,
        items: (data.items || []).map((item) => ({
          product_id: item.product_id,
          unit_product_id: item.unit_product_id,
          quantity: 1,
          unit_price: parseFloat(item.unit_price) || 0,
          notes: item.notes || '',
        })),
      };

      await salesAPI.create(payload);
      toast.success('Sales order created successfully');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Create New Sales Order</DialogTitle>
        <DialogContent dividers>
          {bootstrapLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="customer_id"
                    control={control}
                    rules={{ required: 'Customer required' }}
                    render={({ field }) => (
                      <Autocomplete
                        options={customerOptions}
                        filterOptions={(options, params) => {
                          const filtered = filterCustomers(options.filter((option) => option.id !== NEW_CUSTOMER_OPTION_ID), params);
                          filtered.push(customerOptions[customerOptions.length - 1]);
                          return filtered;
                        }}
                        getOptionLabel={(option) => (
                          option.id === NEW_CUSTOMER_OPTION_ID
                            ? 'New Customer'
                            : `${option.first_name} ${option.last_name} (${option.customer_code})`
                        )}
                        value={customerOptions.find((customer) => customer.id === field.value) || null}
                        onChange={(_, value) => {
                          if (value?.id === NEW_CUSTOMER_OPTION_ID) {
                            onClose();
                            navigate('/customers', {
                              state: {
                                openCreateCustomer: true,
                                returnTo: '/sales',
                                returnSystem: 'car_sales',
                                reopenOrder: true,
                              },
                            });
                            return;
                          }

                          field.onChange(value?.id || '');
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.id === NEW_CUSTOMER_OPTION_ID
                              ? 'New Customer'
                              : `${option.first_name} ${option.last_name} (${option.customer_code})`}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customer *"
                            error={!!errors.customer_id}
                            helperText={errors.customer_id?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="warehouse_id"
                    control={control}
                    rules={{ required: 'Please select a warehouse' }}
                    render={({ fieldState }) => (
                      <FormControl fullWidth size="small" error={!!fieldState.error}>
                        <InputLabel>Warehouse *</InputLabel>
                        <Select
                          value={warehouseId || ''}
                          label="Warehouse *"
                          onChange={(event) => handleWarehouseSelection(event.target.value)}
                        >
                          <MenuItem value=""><em>- Select Warehouse -</em></MenuItem>
                          {warehouses.map((warehouse) => (
                            <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                          ))}
                        </Select>
                        {fieldState.error && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Notes" {...register('notes')} />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Order Items
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Selected Unit</TableCell>
                      <TableCell width={140}>Unit Price</TableCell>
                      <TableCell width={120}>Line Total</TableCell>
                      <TableCell width={40}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, idx) => {
                      const selectedUnits = unitsByRow[field.id] || [];
                      const selectedUnitId = items[idx]?.unit_product_id || '';
                      const selectedUnit = selectedUnits.find((unit) => unit.id === selectedUnitId) || null;
                      const lineTotal = parseFloat(items[idx]?.unit_price) || 0;

                      return (
                        <TableRow key={field.id}>
                          <TableCell sx={{ minWidth: 260 }}>
                            <Controller
                              name={`items.${idx}.product_id`}
                              control={control}
                              rules={{ required: 'Vehicle required' }}
                              render={({ field: productField }) => (
                                <Autocomplete
                                  size="small"
                                  options={filteredProducts}
                                  getOptionLabel={(option) => `${option.brand_name || ''} ${option.name} ${option.model || ''} (${option.sku})`}
                                  value={filteredProducts.find((product) => product.id === productField.value) || null}
                                  onChange={async (_, value) => {
                                    const productId = value?.id || '';
                                    productField.onChange(productId);
                                    setValue(`items.${idx}.unit_product_id`, '');
                                    setValue(`items.${idx}.quantity`, 1);
                                    setValue(`items.${idx}.unit_price`, parseFloat(value?.selling_price) || 0);
                                    await loadUnitsForRow(field.id, productId, warehouseId);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder={warehouseId ? 'Select vehicle' : 'Select warehouse first'}
                                      error={!!errors?.items?.[idx]?.product_id}
                                      helperText={errors?.items?.[idx]?.product_id?.message || (!warehouseId ? 'Choose warehouse first' : '')}
                                    />
                                  )}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 320 }}>
                            <Controller
                              name={`items.${idx}.unit_product_id`}
                              control={control}
                              rules={{ required: 'Select a unit for this sale' }}
                              render={({ field: unitField }) => (
                                <Autocomplete
                                  size="small"
                                  options={selectedUnits.filter(unit => !selectedUnitIds.includes(unit.id) || unit.id === unitField.value)}
                                  loading={!!unitsLoadingByRow[field.id]}
                                  getOptionLabel={unitOptionLabel}
                                  value={selectedUnits.find((unit) => unit.id === unitField.value) || null}
                                  onChange={(_, value) => {
                                    unitField.onChange(value?.id || '');
                                    setValue(
                                      `items.${idx}.unit_price`,
                                      parseFloat(value?.selling_price) || parseFloat(items[idx]?.unit_price) || 0
                                    );
                                  }}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder={warehouseId ? 'Select chassis / unit' : 'Select warehouse first'}
                                      error={!!errors?.items?.[idx]?.unit_product_id}
                                      helperText={
                                        errors?.items?.[idx]?.unit_product_id?.message
                                        || (!warehouseId
                                          ? 'Choose the warehouse first'
                                          : (!unitsLoadingByRow[field.id] && items[idx]?.product_id && selectedUnits.length === 0
                                            ? 'No tracked units available in this warehouse'
                                            : 'Pick the exact unit the customer wants'))
                                      }
                                    />
                                  )}
                                />
                              )}
                            />
                            {selectedUnit && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                {[
                                  selectedUnit.product_name,
                                  selectedUnit.model,
                                  selectedUnit.warehouse_name,
                                  selectedUnit.status?.replace(/_/g, ' '),
                                ].filter(Boolean).join(' • ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              sx={{ width: 120 }}
                              {...register(`items.${idx}.unit_price`, { valueAsNumber: true })}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{fmt(lineTotal)}</TableCell>
                          <TableCell>
                            {fields.length > 1 && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  remove(idx);
                                  setUnitsByRow((prev) => {
                                    const next = { ...prev };
                                    delete next[field.id];
                                    return next;
                                  });
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <Button
                  size="small"
                  startIcon={<Add />}
                  sx={{ mt: 1 }}
                  onClick={() => append({ product_id: '', unit_product_id: '', quantity: 1, unit_price: 0 })}
                >
                  Add Item
                </Button>
              </Paper>

              <Grid container justifyContent="flex-end">
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ bgcolor: '#f5f6fa', borderRadius: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmt(subtotal)}</Typography>
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Discount %"
                          type="number"
                          {...register('discount_percent', { min: 0, max: 100, valueAsNumber: true })}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Tax %"
                          type="number"
                          {...register('tax_percent', { min: 0, valueAsNumber: true })}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="success.main">Discount: -{fmt(discount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Tax: {fmt(tax)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight={700}>Total:</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="primary">{fmt(total)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<ShoppingCart />} disabled={saving || bootstrapLoading}>
            {saving ? <CircularProgress size={20} /> : 'Create Order'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SalesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await salesAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setOrders(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (!location.state?.openCreateOrder && !location.state?.createdCustomer) return;

    setCreatedCustomer(location.state?.createdCustomer || null);
    setCreateOpen(location.state?.openCreateOrder !== false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const columns = [
    { key: 'order_number', label: 'Order #', render: (value) => <Typography variant="body2" fontFamily="monospace" fontWeight={600}>{value}</Typography> },
    { key: 'customer_name', label: 'Customer' },
    { key: 'salesperson_name', label: 'Salesperson' },
    { key: 'order_date', label: 'Date', render: (value) => new Date(value).toLocaleDateString() },
    { key: 'total_amount', label: 'Total', align: 'right', render: (value) => <Typography fontWeight={700}>{fmt(value)}</Typography> },
    { key: 'paid_amount', label: 'Paid', align: 'right', render: (value) => fmt(value) },
    { key: 'balance_due', label: 'Due Balance', align: 'right', render: (value) => <Typography color={parseFloat(value) > 0 ? 'error.main' : 'success.main'} fontWeight={600}>{fmt(value)}</Typography> },
    { key: 'payment_status', label: 'Payment', render: (value) => <StatusChip status={value} /> },
    { key: 'dispatch_status', label: 'Release', render: (value) => <StatusChip status={value} /> },
    { key: 'approval_status', label: 'Approval', render: (value) => <StatusChip status={value} /> },
    { key: 'status', label: 'Order', render: (value) => <StatusChip status={value} /> },
    {
      key: 'id',
      label: 'Actions',
      render: (value) => (
        <Button size="small" variant="outlined" onClick={() => navigate(`/sales/${value}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Sales Orders</Typography>
          <Typography variant="body2" color="text.secondary">{total} total orders</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          New Order
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search orders..."
          size="small"
          sx={{ minWidth: 280 }}
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {ORDER_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        rows={orders}
        loading={loading}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
        emptyMessage="No sales orders found"
      />

      <CreateOrderDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreatedCustomer(null);
        }}
        onSaved={fetchOrders}
        initialCustomer={createdCustomer}
      />
    </Box>
  );
};

export default SalesPage;
